import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  MenuItem,
  Stack,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { toast } from 'react-toastify';
import StoreService from '../components/StoreService';
import {
  createInventoryAsk,
  fetchInventoryAskProducts,
  fetchInventoryAsksByStore,
  isAskActive,
  patchInventoryAskQuantity,
  updateInventoryAskStatus,
} from '../services/inventoryAskService';

const IMAGE_BASE_URL = process.env.REACT_APP_DATA_PIC_PATH || '';

const buildProductImageUrl = (source) => {
  if (!source) return '';
  const trimmed = String(source).trim();
  if (!trimmed) return '';
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  const normalizedBase = IMAGE_BASE_URL.endsWith('/') ? IMAGE_BASE_URL : `${IMAGE_BASE_URL}/`;
  const normalizedSource = trimmed.startsWith('/') ? trimmed.slice(1) : trimmed;
  return `${normalizedBase}${normalizedSource}`;
};

const extractStoreId = (store) =>
  String(store?._id ?? store?.id ?? store?.store_id ?? store?.storeId ?? '');

const askIdOf = (ask) => String(ask?._id ?? ask?.id ?? '');

const productIdOf = (product) =>
  String(product?.product_id ?? product?.productId ?? product?._id ?? product?.id ?? '');

const buildQuantityDraft = (products = []) => {
  const draft = {};
  products.forEach((product) => {
    const id = productIdOf(product);
    if (id) {
      draft[id] = product?.filled_quantity ?? product?.filledQuantity ?? product?.filled ?? '';
    }
  });
  return draft;
};

const formatAskLabel = (ask) => {
  const date = ask?.requested_at || ask?.createdAt;
  const formattedDate = date ? new Date(date).toLocaleString('fr-FR') : 'Date inconnue';
  const status = ask?.status || 'N/A';
  const count = ask?.products_count ?? ask?.productsCount ?? 0;
  return `${formattedDate} - ${status} - ${count} produit(s)`;
};

const InventoryCalculation = () => {
  const roles = useMemo(() => {
    try {
      const parsedRoles = JSON.parse(sessionStorage.getItem('roles') || '[]');
      const normalizedRoles = Array.isArray(parsedRoles) ? parsedRoles : [parsedRoles];
      return normalizedRoles.map((role) => String(role || '').trim().toLowerCase()).filter(Boolean);
    } catch (error) {
      return [];
    }
  }, []);

  const canManageInventoryAsk =
    roles.includes('admin') ||
    roles.includes('manager') ||
    roles.includes('manage_inventory') ||
    roles.includes('inventory_manager');
  const isInventoryContributor =
    roles.includes('operator') || roles.includes('driver') || roles.includes('carrefour');
  const hideCapturedQuantity = isInventoryContributor;
  const defaultStoreId = sessionStorage.getItem('default_store_id') || '';

  const [stores, setStores] = useState([]);
  const [storeId, setStoreId] = useState(defaultStoreId);
  const [asks, setAsks] = useState([]);
  const [selectedAskId, setSelectedAskId] = useState('');
  const [ask, setAsk] = useState(null);
  const [askProducts, setAskProducts] = useState([]);
  const [loadingAsk, setLoadingAsk] = useState(false);
  const [productsLoading, setProductsLoading] = useState(false);
  const [quantities, setQuantities] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [savingByProduct, setSavingByProduct] = useState({});
  const [saveErrors, setSaveErrors] = useState({});
  const [error, setError] = useState('');
  const [reloading, setReloading] = useState(false);
  const [showZeroRecorded, setShowZeroRecorded] = useState(false);
  const [toneFilter, setToneFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('current');
  const saveTimersRef = useRef({});

  const hasPendingSave = useMemo(
    () =>
      Object.keys(saveTimersRef.current).length > 0 ||
      Object.values(savingByProduct).some(Boolean),
    [savingByProduct]
  );

  const hasSaveError = useMemo(
    () => Object.values(saveErrors).some(Boolean),
    [saveErrors]
  );

  useEffect(() => {
    return () => {
      Object.values(saveTimersRef.current).forEach((timer) => clearTimeout(timer));
      saveTimersRef.current = {};
    };
  }, []);

  useEffect(() => {
    if (!canManageInventoryAsk) {
      return;
    }
    const fetchStores = async () => {
      try {
        const data = await StoreService.fetchStores();
        const rawStores = data?.response || data || [];
        const normalized = rawStores
          .map((store) => ({
            id: extractStoreId(store),
            name: store.name || store.store_name || store.label || 'Magasin',
          }))
          .filter((item) => item.id);
        setStores(normalized);
        if (!storeId && normalized.length) {
          setStoreId(normalized[0].id);
        }
      } catch (err) {
        console.error('Erreur lors du chargement des magasins', err);
        toast.error('Impossible de charger les magasins');
      }
    };

    fetchStores();
  }, [canManageInventoryAsk, storeId]);

  const loadAskProducts = useCallback(
    async (askMeta, maybeIsMounted = () => true) => {
      const currentAskId = askIdOf(askMeta);
      if (!currentAskId) {
        if (maybeIsMounted()) {
          setAskProducts([]);
          setQuantities({});
        }
        return;
      }

      if (maybeIsMounted()) {
        setProductsLoading(true);
      }

      try {
        const products = await fetchInventoryAskProducts(currentAskId);
        if (!maybeIsMounted()) return;
        setAsk({ ...askMeta, products });
        setAskProducts(products);
        setQuantities(buildQuantityDraft(products));
        setSaveErrors({});
      } catch (err) {
        console.error('Erreur lors du chargement des produits de la demande inventaire', err);
        const message =
          err?.response?.data?.error ||
          err?.message ||
          'Impossible de charger les produits de la demande';
        if (maybeIsMounted()) {
          setAsk({ ...askMeta, products: [] });
          setAskProducts([]);
          setQuantities({});
          setError(message);
          toast.error(message);
        }
      } finally {
        if (maybeIsMounted()) {
          setProductsLoading(false);
        }
      }
    },
    []
  );

  const loadAsk = useCallback(
    async (maybeIsMounted = () => true, preferredAskId = '') => {
      const isMountedFn = typeof maybeIsMounted === 'function' ? maybeIsMounted : () => true;
      const canReadInventory = canManageInventoryAsk || isInventoryContributor;
      if (!canReadInventory) {
        if (isMountedFn()) {
          setAsks([]);
          setAsk(null);
          setAskProducts([]);
          setError('');
          setLoadingAsk(false);
          setReloading(false);
        }
        return;
      }

      if (!storeId) {
        if (isMountedFn()) {
          setAsks([]);
          setAsk(null);
          setAskProducts([]);
          setError(
            isInventoryContributor
              ? 'Aucun magasin par defaut configure. Selectionnez un magasin pour continuer.'
              : ''
          );
          setLoadingAsk(false);
          setReloading(false);
        }
        return;
      }

      if (isMountedFn()) {
        setLoadingAsk(true);
        setReloading(true);
        setError('');
      }

      try {
        const list = await fetchInventoryAsksByStore(storeId);
        if (!isMountedFn()) return;

        const normalized = Array.isArray(list) ? list : [];
        const visibleAsks = isInventoryContributor
          ? normalized.filter((item) => isAskActive(item))
          : normalized;
        const nextSelected =
          visibleAsks.find((item) => askIdOf(item) === preferredAskId) ||
          visibleAsks.find((item) => isAskActive(item)) ||
          visibleAsks[0] ||
          null;

        setAsks(visibleAsks);
        setSelectedAskId(askIdOf(nextSelected));

        if (!nextSelected) {
          setAsk(null);
          setAskProducts([]);
          setQuantities({});
          setError(
            isInventoryContributor
              ? 'Seuls les inventaires en cours sont accessibles pour votre magasin par defaut.'
              : ''
          );
          return;
        }

        setAsk({ ...nextSelected, products: [] });
        setAskProducts([]);
        setQuantities({});
        await loadAskProducts(nextSelected, isMountedFn);
      } catch (err) {
        console.error('Erreur lors du chargement de la demande inventaire', err);
        const message =
          err?.response?.data?.error ||
          err?.message ||
          "Impossible de recuperer l'inventaire demande";
        if (isMountedFn()) {
          setError(message);
          setAsks([]);
          setAsk(null);
          setAskProducts([]);
          toast.error(message);
        }
      } finally {
        if (isMountedFn()) {
          setLoadingAsk(false);
          setReloading(false);
        }
      }
    },
    [canManageInventoryAsk, isInventoryContributor, loadAskProducts, storeId]
  );

  useEffect(() => {
    let mounted = true;
    loadAsk(() => mounted, '');
    return () => {
      mounted = false;
    };
  }, [loadAsk]);

  const handleAskSelection = async (nextAskId) => {
    setSelectedAskId(nextAskId);
    const nextAsk = asks.find((item) => askIdOf(item) === nextAskId);
    setAsk(nextAsk ? { ...nextAsk, products: [] } : null);
    setAskProducts([]);
    setQuantities({});
    if (nextAsk) {
      await loadAskProducts(nextAsk);
    }
  };

  const handleCreateAsk = async () => {
    if (!storeId) {
      toast.error('Selectionnez un magasin avant de lancer un inventaire');
      return;
    }

    setSubmitting(true);
    try {
      const newAsk = await createInventoryAsk({ store_id: storeId });
      toast.success("Demande d'inventaire creee");
      await loadAsk(() => true, askIdOf(newAsk));
    } catch (err) {
      const status = err?.response?.status;
      if (status === 409) {
        toast.info('Un inventaire est deja en cours pour ce magasin');
      } else {
        console.error('Erreur lors de la creation de la demande inventaire', err);
        const message =
          err?.response?.data?.error ||
          err?.message ||
          'Impossible de creer une nouvelle demande';
        toast.error(message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const mergeSavedProduct = (productId, response) => {
    const updatedProduct = response?.updated_product || response?.response?.updated_product || null;
    const nextStatus = response?.status || response?.response?.status;

    if (nextStatus) {
      setAsk((prev) => (prev ? { ...prev, status: nextStatus } : prev));
      setAsks((prev) =>
        prev.map((item) => (askIdOf(item) === askIdOf(ask) ? { ...item, status: nextStatus } : item))
      );
    }

    if (!updatedProduct) return;

    setAskProducts((prev) =>
      prev.map((product) =>
        productIdOf(product) === productId
          ? {
              ...product,
              filled_quantity: updatedProduct.filled_quantity,
              user_history: updatedProduct.user_history || product.user_history,
            }
          : product
      )
    );
  };

  const saveQuantity = useCallback(
    async (productId, value) => {
      if (!ask?._id || ask?.status === 'completed') return;
      setSavingByProduct((prev) => ({ ...prev, [productId]: true }));
      setSaveErrors((prev) => ({ ...prev, [productId]: '' }));

      try {
        const updated = await patchInventoryAskQuantity(ask._id, productId, value);
        mergeSavedProduct(productId, updated);
        toast.success('Quantite mise a jour');
      } catch (err) {
        console.error('Erreur mise a jour quantite inventaire', err);
        const message =
          err?.response?.data?.error || err?.message || 'Impossible de mettre a jour';
        setSaveErrors((prev) => ({ ...prev, [productId]: message }));
        toast.error(message);
      } finally {
        setSavingByProduct((prev) => ({ ...prev, [productId]: false }));
      }
    },
    [ask]
  );

  const handleQuantityChange = (productId, value) => {
    setQuantities((prev) => ({ ...prev, [productId]: value }));
    setSaveErrors((prev) => ({ ...prev, [productId]: '' }));

    if (saveTimersRef.current[productId]) {
      clearTimeout(saveTimersRef.current[productId]);
    }

    saveTimersRef.current[productId] = setTimeout(() => {
      delete saveTimersRef.current[productId];
      void saveQuantity(productId, value);
    }, 3000);
  };

  const handleComplete = async () => {
    if (!ask?._id) return;
    if (hasPendingSave) {
      toast.warning('Veuillez attendre la sauvegarde automatique avant de terminer.');
      return;
    }
    if (hasSaveError) {
      toast.error('Corrigez les erreurs de sauvegarde avant de terminer.');
      return;
    }

    setSubmitting(true);
    try {
      const updated = await updateInventoryAskStatus(ask._id, 'completed');
      setAsk((prev) => ({ ...(prev || {}), ...(updated || {}), products: askProducts }));
      setAsks((prev) =>
        prev.map((item) => (askIdOf(item) === askIdOf(updated) ? { ...item, ...updated } : item))
      );
      toast.success('Inventaire marque comme termine');
    } catch (err) {
      console.error("Erreur lors de la cloture de l'inventaire", err);
      const message = err?.response?.data?.error || err?.message || 'Impossible de cloturer';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const enrichedProducts = useMemo(() => {
    return askProducts.map((product) => {
      const id = productIdOf(product);
      const image =
        buildProductImageUrl(product?.url_product) ||
        product?.urlImage ||
        buildProductImageUrl(product?.image) ||
        '';
      return {
        id,
        name: product?.name || product?.title || 'Produit',
        available:
          product?.qty_available_inventory ??
          product?.available_quantity ??
          product?.quantity ??
          0,
        filled: product?.filled_quantity ?? product?.filledQuantity ?? product?.filled ?? null,
        image,
      };
    });
  }, [askProducts]);

  const getCardTone = (product) => {
    if (ask?.status !== 'completed') return {};

    const existing = Number(product?.available ?? 0);
    const filledValue = product?.filled ?? null;
    const filled = filledValue === null || filledValue === undefined ? null : Number(filledValue);

    if (Number.isNaN(existing) || filled === null || Number.isNaN(filled)) {
      return {};
    }

    if (filled < existing) {
      return { bgcolor: 'error.light', borderColor: 'error.main', tone: 'less' };
    }

    if (filled > existing) {
      return { bgcolor: 'warning.light', borderColor: 'warning.main', tone: 'more' };
    }

    return { bgcolor: 'success.light', borderColor: 'success.main', tone: 'equal' };
  };

  const accuracyStats = useMemo(() => {
    const comparable = enrichedProducts.filter((product) => {
      const available = Number(product?.available ?? 0);
      const filledValue = product?.filled;
      const filled = filledValue === null || filledValue === undefined ? null : Number(filledValue);
      return Number.isFinite(available) && filled !== null && Number.isFinite(filled);
    });

    const matching = comparable.filter((product) => Number(product.filled) === Number(product.available));
    const less = comparable.filter((product) => Number(product.filled) < Number(product.available));
    const more = comparable.filter((product) => Number(product.filled) > Number(product.available));
    const nonMatching = less.length + more.length;
    const accuracy = comparable.length ? Math.max(0, (matching.length / comparable.length) * 100) : 0;
    const nonMatchingRate = comparable.length ? (nonMatching / comparable.length) * 100 : 0;

    return {
      total: enrichedProducts.length,
      comparable: comparable.length,
      matching: matching.length,
      less: less.length,
      more: more.length,
      nonMatching,
      accuracy,
      nonMatchingRate,
      nonMatchingProducts: comparable.filter((product) => Number(product.filled) !== Number(product.available)),
    };
  }, [enrichedProducts]);

  const filteredProducts = useMemo(() => {
    let list = enrichedProducts.filter((product) => Number(product.available) > 0);

    if (showZeroRecorded) {
      list = list.filter((product) => {
        const filled = product?.filled;
        if (filled === null || filled === undefined) return false;
        const numeric = Number(filled);
        return !Number.isNaN(numeric) && numeric === 0;
      });
    }

    if (toneFilter !== 'all' && ask?.status === 'completed') {
      list = list.filter((product) => getCardTone(product).tone === toneFilter);
    }

    return list;
  }, [ask?.status, enrichedProducts, showZeroRecorded, toneFilter]);

  const statusChip = ask?.status ? (
    <Chip
      size="small"
      color={isAskActive(ask) ? 'warning' : ask.status === 'completed' ? 'success' : 'default'}
      label={`Statut : ${ask.status}`}
    />
  ) : null;

  return (
    <Stack spacing={3} sx={{ p: { xs: 2, md: 4 } }}>
      <Typography variant="h4" component="h1">
        Inventaire Calcul
      </Typography>

      <Card>
        <CardHeader
          title="Magasin"
          subheader="Demarrez, completez ou consultez une demande d'inventaire"
          action={statusChip}
        />
        <CardContent>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
            {canManageInventoryAsk ? (
              <TextField
                select
                label="Magasin"
                value={storeId}
                onChange={(event) => {
                  setStoreId(event.target.value);
                  setSelectedAskId('');
                }}
                sx={{ minWidth: 240 }}
              >
                {stores.map((store) => (
                  <MenuItem key={store.id} value={store.id}>
                    {store.name}
                  </MenuItem>
                ))}
              </TextField>
            ) : (
              <Box>
                <Typography variant="subtitle2">Magasin</Typography>
                <Typography variant="body1">{storeId || 'Aucun magasin configure'}</Typography>
              </Box>
            )}

            <Box sx={{ flexGrow: 1 }} />

            {canManageInventoryAsk && (
              <Button
                variant="contained"
                onClick={handleCreateAsk}
                disabled={submitting || !storeId || asks.some((item) => isAskActive(item)) || loadingAsk}
              >
                Lancer un inventaire
              </Button>
            )}
            <Button
              variant="outlined"
              onClick={() => loadAsk(undefined, selectedAskId)}
              disabled={loadingAsk || reloading}
            >
              Recharger
            </Button>
            {canManageInventoryAsk && ask?.status !== 'completed' && ask && (
              <Button
                variant="outlined"
                color="success"
                onClick={handleComplete}
                disabled={submitting || hasPendingSave || hasSaveError || !ask?._id}
              >
                Terminer
              </Button>
            )}
          </Stack>
        </CardContent>
      </Card>

      {canManageInventoryAsk && (
        <Card>
          <Tabs
            value={activeTab}
            onChange={(event, value) => setActiveTab(value)}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab value="current" label="Inventaire" />
            <Tab value="history" label="Historique & precision" />
          </Tabs>
        </Card>
      )}

      {!(canManageInventoryAsk || isInventoryContributor) ? (
        <Card>
          <CardContent>
            <Typography color="error" fontWeight="bold">
              Acces reserve aux administrateurs, managers et contributeurs inventaire.
            </Typography>
          </CardContent>
        </Card>
      ) : loadingAsk ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Typography color="error">{error}</Typography>
      ) : !ask ? (
        <Card>
          <CardContent>
            <Typography>
              {canManageInventoryAsk
                ? 'Aucun inventaire pour ce magasin. Creez une demande pour commencer.'
                : 'Aucun inventaire en cours pour votre magasin par defaut. Contactez un responsable lorsque le statut passe a ongoing.'}
            </Typography>
          </CardContent>
        </Card>
      ) : canManageInventoryAsk && activeTab === 'history' ? (
        <Card>
          <CardHeader
            title="Historique & precision"
            subheader={selectedAskId ? formatAskLabel(ask) : 'Selectionnez une demande historique'}
            action={statusChip}
          />
          <Divider />
          <CardContent>
            {productsLoading ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress />
              </Box>
            ) : (
              <Stack spacing={3}>
                <TextField
                  select
                  label="Historique inventaire"
                  value={selectedAskId}
                  onChange={(event) => handleAskSelection(event.target.value)}
                  sx={{ maxWidth: 520 }}
                  disabled={loadingAsk || !asks.length}
                  helperText="Selectionnez une demande pour afficher son historique et sa precision."
                >
                  {asks.map((item) => (
                    <MenuItem key={askIdOf(item)} value={askIdOf(item)}>
                      {formatAskLabel(item)}
                    </MenuItem>
                  ))}
                </TextField>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={2.4}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="caption" color="text.secondary">
                          Precision
                        </Typography>
                        <Typography variant="h4" color="success.main" fontWeight="bold">
                          {accuracyStats.accuracy.toFixed(1)}%
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2.4}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="caption" color="text.secondary">
                          Non matching
                        </Typography>
                        <Typography variant="h4" color="error.main" fontWeight="bold">
                          {accuracyStats.nonMatchingRate.toFixed(1)}%
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2.4}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="caption" color="text.secondary">
                          Produits compares
                        </Typography>
                        <Typography variant="h4" fontWeight="bold">
                          {accuracyStats.comparable}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2.4}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="caption" color="text.secondary">
                          Ecarts -
                        </Typography>
                        <Typography variant="h4" color="error.main" fontWeight="bold">
                          {accuracyStats.less}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {accuracyStats.comparable ? ((accuracyStats.less / accuracyStats.comparable) * 100).toFixed(1) : '0.0'}%
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2.4}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="caption" color="text.secondary">
                          Ecarts +
                        </Typography>
                        <Typography variant="h4" color="warning.main" fontWeight="bold">
                          {accuracyStats.more}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {accuracyStats.comparable ? ((accuracyStats.more / accuracyStats.comparable) * 100).toFixed(1) : '0.0'}%
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>

                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems="center">
                  <Chip color="success" label={`${accuracyStats.matching} matching`} />
                  <Chip color="default" label={`${accuracyStats.nonMatching} non matching`} />
                  <Chip label={`${accuracyStats.total} produit(s) total`} />
                </Stack>

                <Card variant="outlined">
                  <CardHeader title="Produits non matching (+/-)" />
                  <Divider />
                  <CardContent>
                    {accuracyStats.nonMatchingProducts.length ? (
                      <Stack spacing={1.5}>
                        {accuracyStats.nonMatchingProducts.map((product) => {
                          const available = Number(product.available || 0);
                          const filled = Number(product.filled || 0);
                          const diff = filled - available;
                          return (
                            <Stack
                              key={product.id}
                              direction={{ xs: 'column', md: 'row' }}
                              spacing={1}
                              alignItems={{ xs: 'flex-start', md: 'center' }}
                              justifyContent="space-between"
                              sx={{
                                p: 1.5,
                                border: '1px solid',
                                borderColor: diff < 0 ? 'error.light' : 'warning.light',
                                borderRadius: 2,
                                bgcolor: diff < 0 ? '#fff5f5' : '#fff8e1',
                              }}
                            >
                              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
                                {product.image ? (
                                  <Box
                                    component="img"
                                    src={product.image}
                                    alt={product.name}
                                    sx={{ width: 52, height: 52, objectFit: 'cover', borderRadius: 1.5 }}
                                    onError={(event) => {
                                      event.target.onerror = null;
                                      event.target.src = 'https://via.placeholder.com/52x52?text=Image';
                                    }}
                                  />
                                ) : (
                                  <Box
                                    sx={{
                                      width: 52,
                                      height: 52,
                                      bgcolor: 'grey.100',
                                      borderRadius: 1.5,
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      color: 'text.secondary',
                                      fontSize: 10,
                                      flex: '0 0 auto',
                                    }}
                                  >
                                    Image
                                  </Box>
                                )}
                                <Typography fontWeight="bold" noWrap sx={{ minWidth: 0 }}>
                                  {product.name}
                                </Typography>
                              </Stack>
                              <Stack direction="row" spacing={1} alignItems="center">
                                <Chip size="small" label={`Capture: ${available}`} />
                                <Chip size="small" label={`Compte: ${filled}`} />
                                <Chip
                                  size="small"
                                  color={diff < 0 ? 'error' : 'warning'}
                                  label={`${diff > 0 ? '+' : ''}${diff}`}
                                />
                              </Stack>
                            </Stack>
                          );
                        })}
                      </Stack>
                    ) : (
                      <Typography color="success.main" fontWeight="bold">
                        Aucun ecart detecte pour les produits compares.
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Stack>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader
            title="Produits a compter"
            subheader={`Total: ${askProducts.length || ask?.products_count || 0} produit(s)`}
          />
          <Divider />
          <CardContent>
            {canManageInventoryAsk && (
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={2}
                alignItems={{ xs: 'flex-start', md: 'center' }}
                justifyContent="space-between"
                sx={{ mb: 2 }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={showZeroRecorded}
                      onChange={(event) => setShowZeroRecorded(event.target.checked)}
                    />
                  }
                  label="Quantite enregistree = 0"
                />

                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="body2">Filtrer par stock</Typography>
                  <ToggleButtonGroup
                    value={toneFilter}
                    exclusive
                    size="small"
                    onChange={(event, value) => {
                      if (value) {
                        setToneFilter(value);
                      }
                    }}
                    disabled={ask?.status !== 'completed'}
                  >
                    <ToggleButton value="all">Tous</ToggleButton>
                    <ToggleButton value="less" sx={{ color: 'error.main' }}>
                      Rouge
                    </ToggleButton>
                    <ToggleButton value="equal" sx={{ color: 'success.main' }}>
                      Vert
                    </ToggleButton>
                    <ToggleButton value="more" sx={{ color: 'warning.main' }}>
                      Jaune
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Stack>
              </Stack>
            )}

            {productsLoading ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress />
              </Box>
            ) : (
              <Grid container spacing={2}>
                {filteredProducts.map((product) => {
                  const tone = getCardTone(product);
                  const isSaving = !!savingByProduct[product.id];
                  const saveError = saveErrors[product.id];
                  return (
                    <Grid item xs={12} sm={6} md={4} key={product.id}>
                      <Stack
                        direction="row"
                        spacing={2}
                        alignItems="center"
                        sx={{
                          p: 2,
                          border: '1px solid',
                          borderColor: saveError ? 'error.main' : tone.borderColor || 'divider',
                          bgcolor: tone.bgcolor || 'background.paper',
                          borderRadius: 2,
                        }}
                      >
                        {product.image ? (
                          <Box
                            component="img"
                            src={product.image}
                            alt={product.name}
                            sx={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 1 }}
                            onError={(event) => {
                              event.target.onerror = null;
                              event.target.src = 'https://via.placeholder.com/64x64?text=Image';
                            }}
                          />
                        ) : (
                          <Box
                            sx={{
                              width: 64,
                              height: 64,
                              bgcolor: 'grey.100',
                              borderRadius: 1,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: 'text.secondary',
                              fontSize: 12,
                            }}
                          >
                            Image
                          </Box>
                        )}

                        <Stack spacing={1} sx={{ flexGrow: 1, minWidth: 0 }}>
                          <Typography variant="subtitle1" noWrap>
                            {product.name}
                          </Typography>
                          {!hideCapturedQuantity && (
                            <Typography variant="body2" color="text.secondary">
                              Quantite disponible capturee : {product.available}
                            </Typography>
                          )}
                          <Stack direction="row" spacing={1} alignItems="center">
                            <TextField
                              type="number"
                              size="small"
                              label="Quantite comptee"
                              value={quantities[product.id] ?? ''}
                              onChange={(event) => handleQuantityChange(product.id, event.target.value)}
                              sx={{ width: 150 }}
                              inputProps={{ min: 0 }}
                              disabled={ask?.status === 'completed'}
                              error={Boolean(saveError)}
                              helperText={saveError || (isSaving ? 'Sauvegarde...' : 'Auto 3s')}
                            />
                            {isSaving && <CircularProgress size={20} />}
                          </Stack>
                          {product.filled !== null && product.filled !== undefined && (
                            <Typography variant="caption" color="text.secondary">
                              Derniere valeur enregistree : {product.filled}
                            </Typography>
                          )}
                        </Stack>
                      </Stack>
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </CardContent>
        </Card>
      )}
    </Stack>
  );
};

export default InventoryCalculation;
